import React, { useState } from "react";
import { graphql } from "gatsby";
import Page from "~/models/page";
import News from "~/models/news";
import SEO from "~/views/components/generic/seo";
import structuredData from "~/helpers/structured-data";
import Frame from "~/views/layout/frame";
import ListItem from "~/views/components/news/list-item";
import Navigation from "~/views/compositions/navigation";
import Breadcrumbs from "~/views/compositions/breadcrumbs";

type Props = {
  data: any; // Updated to use any since the generated types may not be available yet
};

const NewsListPage = ({ data }: Props) => {
  const page = new Page(data.page);
  const defaultPageInfo = data.news.pageInfo;
  const defaultNews = data.news.nodes.map((news: any) => new News(news));

  const [news, setNews] = useState(defaultNews);
  const [pageInfo, setPageInfo] = useState(defaultPageInfo);

  if (defaultPageInfo.currentPage > 1) {
    page.url.pathname = `/news/pages/${pageInfo.currentPage}/`;
  }

  const onLoadMore = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    try {
      const response = await fetch(
        `/page-data/news/pages/${pageInfo.currentPage + 1}/page-data.json`
      ).then((response) => response.json());

      const nextPage = response.result.data.news.nodes.map(
        (news: any) => new News(news)
      );

      setNews(news.concat(nextPage));
      setPageInfo(response.result.data.news.pageInfo);
    } catch (error) {}
  };

  return (
    <Frame>
      <Navigation />
      <Breadcrumbs urls={page.breadcrumb} />
      {/* Header Section */}
      <section className="flex flex-col gap-8 py-5 w-full mx-auto px-container">
        <div className="flex flex-col gap-4">
          <h1 className="font-[800] text-[40px] leading-[74px] palt">News</h1>
        </div>
      </section>
      <div className="flex flex-col items-start w-full palt tracking-[0.02em] px-0 max-w-[1096px] mx-auto mb-20">
        {news.map((n: any) => (
          <ListItem
            key={n.id}
            url={n.url}
            createdAt={n.createdAt}
            title={n.title}
            description={n.description}
          />
        ))}
      </div>
    </Frame>
  );
};

export default NewsListPage;

export const Head = ({ data }: Props) => {
  const page = new Page(data.page);
  const defaultPageInfo = data.news.pageInfo;
  const sd = structuredData(page);

  if (defaultPageInfo.currentPage > 1) {
    page.url.pathname = `/news/pages/${data.news.pageInfo.currentPage}/`;
  }

  return (
    <SEO page={page} sd={sd}>
      {defaultPageInfo.hasPreviousPage && (
        <link
          rel="prev"
          href={
            defaultPageInfo.currentPage == 2
              ? `${process.env.GATSBY_SITE_URL}/news/`
              : `${process.env.GATSBY_SITE_URL}/news/pages/${
                  defaultPageInfo.currentPage - 1
                }/`
          }
        />
      )}

      {defaultPageInfo.hasNextPage && (
        <link
          rel="next"
          href={`${process.env.GATSBY_SITE_URL}/news/pages/${
            defaultPageInfo.currentPage + 1
          }/`}
        />
      )}
    </SEO>
  );
};

export const query = graphql`
  query NewsListPage($skip: Int, $limit: Int) {
    page: pageMdx(
      filter: { frontmatter: { url: { pathname: { eq: "/news/" } } } }
    ) {
      id
      frontmatter {
        id
        title
        description
        breadcrumb {
          ...UrlFields
        }
        url {
          ...UrlFields
        }
        type
        releasedAt
        updatedAt
      }
    }
    news: allPageMdx(
      filter: { frontmatter: { type: { eq: "News" } } }
      sort: { fields: { releasedAt: DESC } }
      skip: $skip
      limit: $limit
    ) {
      nodes {
        ...PageThumbnailFields
      }
      pageInfo {
        currentPage
        hasNextPage
        hasPreviousPage
        pageCount
      }
    }
  }
`;
